import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Popover } from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';

import SimpleTaskDialog from '../SimpleTaskDialog';
import CrmTaskDialog from '../../CrmTask/CrmTaskDialog';
import { ButtonUI, DataGridIdentifiers, DatagridPremiumUi } from '../../Interface';
import projectService from '../../../service/project.service';
import { ProjectHistoryType, ProjectHistoryTypeList } from '../../../config/projectHistory';
import ProjectHistoryAttachment from './ProjectHistoryAttachment';
import ProjectTaskListReport from './ProjectTaskListReport';
import DailyReportDialog from '../../DailyReport/DailyReportDialog';
import { dataGridPremiumColumns } from '../../../utils/helper';
import { GridToolbarContainer } from '@mui/x-data-grid-premium';

function EditToolbar(props) {
  const { handlePopoverOpen, disabled } = props;
  return (
    <GridToolbarContainer>
      <ButtonUI
        text="Új felvitel"
        className="bg-success mb-1"
        size="sm"
        onClick={handlePopoverOpen}
        disabled={disabled}
      />
    </GridToolbarContainer>
  );
}

const ProjectHistory = ({ selectedValues, setSelectedValues, disabled, setRefetchTable }) => {
  const [selectedRow, setSelectedRow] = useState(null);
  const [rows, setRows] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [taskChanged, setTaskChanged] = useState(false);

  const openPopover = Boolean(anchorEl);

  useEffect(() => {
    setRows(selectedValues.histories?.length ? selectedValues.histories : []);
  }, [selectedValues.histories]);

  useEffect(() => {
    if (taskChanged) {
      getProjectById();
      setRefetchTable(true);
    }
  }, [taskChanged]);

  const getProjectById = () => {
    if (selectedValues.projectId) {
      projectService.getProjectById(selectedValues.projectId).then((data) => {
        setSelectedValues(data);
        setTaskChanged(false);
      });
    }
  };

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleOpen = (row) => {
    projectService.getHistoryItemById(row.taskType, row.id).then((data) => {
      setSelectedRow({ taskType: row.taskType, task: data });
    });
  };

  const renderDialog = () => {
    if (!selectedRow) return null;

    const { taskType, task } = selectedRow;

    const commonProps = {
      open: true,
      handleClose: () => setSelectedRow(null),
      task: task ?? {},
      setSelectedRow,
      setRows,
      setTaskChanged,
      selectedValues,
      parentData: {
        projectId: selectedValues.projectId,
        companyId: selectedValues.companyId,
      },
    };

    switch (taskType) {
      case ProjectHistoryType.CRM:
        return <CrmTaskDialog {...commonProps} />;
      case ProjectHistoryType.TASK:
        return <SimpleTaskDialog {...commonProps} />;
      case ProjectHistoryType.DAILYREPORT:
        return <DailyReportDialog {...commonProps} />;
      case ProjectHistoryType.TASKLISTREPORT:
        return <ProjectTaskListReport {...commonProps} />;
      default:
        return null;
    }
  };

  const columns = dataGridPremiumColumns([
    {
      field: 'startDate',
      headerName: 'Kezdő időpont',
      type: 'date',
      valueGetter: (_data, row) => {
        return row.startDate ? new Date(row.startDate) : null;
      },
      renderCell: (data) => {
        return data.row.startDate ? dayjs(data.row.startDate).format('YYYY.MM.DD HH:mm') : null;
      },
    },
    {
      field: 'taskType',
      headerName: 'Objektum',
      type: 'string',
      valueGetter: (_data, row) => {
        return ProjectHistoryTypeList.find((item) => item.value === row.taskType)?.label;
      },
    },
    {
      field: 'name',
      headerName: 'Feladat neve',
      type: 'string',
    },
    {
      field: 'type',
      headerName: 'Típus',
      type: 'string',
      valueGetter: (_data, row) => {
        return row?.type?.itemName;
      },
    },
    {
      field: 'createdBy',
      headerName: 'Tulajdonos',
      type: 'string',
      valueGetter: (_data, row) => {
        return row?.user?.userName;
      },
    },
    {
      field: 'description',
      headerName: 'Megjegyzés',
      type: 'string',
    },
    {
      field: 'more',
      headerName: 'Megtekint',
      type: 'actions',
      renderCell: (params) => {
        return params.row.taskType === ProjectHistoryType.ATTACHMENT ? (
          <ProjectHistoryAttachment data={params.row} />
        ) : (
          <RemoveRedEyeIcon color="black" onClick={() => handleOpen(params.row)} />
        );
      },
    },
  ]);

  return (
    <div className="max-h-[400px] overflow-y-scroll overflow-x-hidden custom-scrollbar -mt-3">
      <Popover
        open={openPopover}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      >
        <div className="p-4 flex gap-2">
          <ButtonUI
            text="Új esemény"
            className="bg-warning"
            size="sm"
            onClick={() => {
              setSelectedRow({ taskType: ProjectHistoryType.CRM, task: {} });
              handlePopoverClose();
            }}
          />
          <ButtonUI
            text="Új feladat"
            className="bg-warning"
            size="sm"
            onClick={() => {
              setSelectedRow({ taskType: ProjectHistoryType.TASK, task: {} });
              handlePopoverClose();
            }}
          />
        </div>
      </Popover>
      <DatagridPremiumUi
        height={400}
        datagridIdentifier={DataGridIdentifiers.ProjectHistory}
        rows={rows}
        rowCount={rows?.length}
        columns={[...columns]}
        disableRowSelectionOnClick={!disabled}
        getRowHeight={() => 'auto'}
        headerFilters={false}
        slots={{
          toolbar: EditToolbar,
        }}
        slotProps={{
          toolbar: {
            handlePopoverOpen,
            disabled,
          },
        }}
      />
      {renderDialog()}
    </div>
  );
};

export default ProjectHistory;
